import {
  Component,
  inject,
  output,
  signal,
  computed,
  effect,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import {
  selectEnvironmentType,
  selectEnvironmentLabel,
  selectEnvironmentInfo,
  selectBannerDismissed,
} from '@core/state/environment';
import { DatabaseDisplayNamePipe } from '@shared/pipes/database-display-name/database-display-name.pipe';

/**
 * Small navbar indicator that appears after the environment banner is dismissed.
 * Shows environment color with warning icon, click to reopen warning dialog.
 */
@Component({
  selector: 'app-environment-chip',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule],
  template: `
    @if (showChip()) {
      <button
        mat-icon-button
        class="environment-chip"
        [class]="'environment-chip--' + environmentType()"
        [class.environment-chip--animate]="shouldAnimate()"
        [matTooltip]="tooltipText()"
        matTooltipPosition="below"
        (click)="clicked.emit()"
        aria-label="View environment details"
      >
        <mat-icon>warning</mat-icon>
      </button>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .environment-chip {
        width: 32px;
        height: 32px;
        min-width: 32px;
        border-radius: 50%;
        color: var(--syrf-on-env);
        transition:
          transform 0.15s ease,
          filter 0.15s ease;
      }

      .environment-chip:hover {
        filter: brightness(1.1);
      }

      .environment-chip mat-icon {
        font-size: 18px;
        height: 18px;
        width: 18px;
      }

      .environment-chip--staging {
        background: var(--syrf-env-staging);
      }

      .environment-chip--preview {
        background: var(--syrf-env-preview);
      }

      .environment-chip--development {
        background: var(--syrf-env-development);
      }

      .environment-chip--local {
        background: var(--syrf-env-local);
      }

      .environment-chip--production {
        display: none;
      }

      /* Pulse animation for attention on first render */
      @keyframes env-chip-pulse {
        0%,
        100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.15);
        }
      }

      .environment-chip--animate {
        animation: env-chip-pulse 0.6s ease-in-out 3;
      }
    `,
  ],
})
export class EnvironmentChipComponent implements OnDestroy {
  private readonly store = inject(Store);
  private readonly dbDisplayNamePipe = new DatabaseDisplayNamePipe();

  // Store selectors as signals
  protected readonly environmentType = this.store.selectSignal(
    selectEnvironmentType
  );
  protected readonly environmentLabel = this.store.selectSignal(
    selectEnvironmentLabel
  );
  protected readonly info = this.store.selectSignal(selectEnvironmentInfo);
  protected readonly bannerDismissed = this.store.selectSignal(
    selectBannerDismissed
  );

  // Animation state - pulse 3 times when first shown
  protected readonly shouldAnimate = signal(false);
  private hasAnimated = false;
  private animationTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /** Emitted when user clicks the chip */
  readonly clicked = output<void>();

  /**
   * Show chip when:
   * 1. Not production environment
   * 2. Banner has been dismissed by user
   */
  protected readonly showChip = computed(() => {
    const info = this.info();
    return info != null && !info.isProduction && this.bannerDismissed();
  });

  /**
   * Tooltip text showing environment and database.
   * Uses DatabaseDisplayNamePipe to transform "syrftest" to "Production Database".
   */
  protected readonly tooltipText = computed(() => {
    const info = this.info();
    if (!info) return 'Loading...';

    const envName = info.environment?.toLowerCase();
    const isLocal = envName === 'development' || envName === 'local';

    const parts: string[] = [];

    // Environment label
    if (isLocal) {
      parts.push('Local Development');
    } else if (info.prNumber) {
      parts.push(`Preview PR #${info.prNumber}`);
    } else if (info.environment) {
      parts.push(info.environment);
    }

    // Database (with transformation using server-derived databaseType)
    if (info.databaseName && info.databaseName !== 'unknown') {
      parts.push(
        this.dbDisplayNamePipe.transform(info.databaseName, info.databaseType)
      );
    }

    return parts.join(' · ');
  });

  constructor() {
    // Trigger animation when chip becomes visible for the first time
    effect(() => {
      if (this.showChip() && !this.hasAnimated) {
        this.hasAnimated = true;
        // Small delay ensures DOM is ready
        this.animationTimeoutId = setTimeout(
          () => this.shouldAnimate.set(true),
          100
        );
      }
    });
  }

  ngOnDestroy(): void {
    if (this.animationTimeoutId !== null) {
      clearTimeout(this.animationTimeoutId);
    }
  }
}
